import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ConfirmDialogService } from '../../dialog/confirm-dialog/confirm-dialog.service';
import {
  GeneralLedgerGroupReference,
  GeneralledgerGroupService,
} from './generalledgergroup.service';

/** Dependencies required to run the shared general ledger group delete flow. */
export interface DeleteGeneralLedgerGroupOptions {
  /** Id of the general ledger group to delete. */
  id: string;
  service: GeneralledgerGroupService;
  dialogService: ConfirmDialogService;
  destroyRef: DestroyRef;
  /** Called after the general ledger group has been removed successfully. */
  onDeleted: () => void;
}

/**
 * Runs the shared "Verwijderen" flow for a general ledger group:
 * checks for existing references and either shows a blocking dialog
 * or asks for confirmation before deleting.
 */
export function deleteGeneralLedgerGroupWithChecks(
  options: DeleteGeneralLedgerGroupOptions,
): void {
  const { id, service, dialogService, destroyRef } = options;

  service
    .getReferenceCountForDeleteCheck(id)
    .pipe(take(1), takeUntilDestroyed(destroyRef))
    .subscribe({
      next: (references) => {
        const blocking = references.filter((reference) => reference.count > 0);
        if (blocking.length > 0) {
          showDeleteNotPossibleDialog(dialogService, blocking);
        } else {
          confirmAndDeleteGeneralLedgerGroup(options);
        }
      },
      error: () => undefined,
    });
}

function showDeleteNotPossibleDialog(
  dialogService: ConfirmDialogService,
  references: GeneralLedgerGroupReference[],
): void {
  const lines = references.map(
    (reference) => `- ${reference.tablename} heeft ${reference.count} verwijzingen`,
  );
  const message = [
    'Kan Grootboekrekeningengroep niet verwijderen de volgende verwijzingen bestaan nog:',
    ...lines,
  ].join('\n');

  dialogService.open({
    width: '300px',
    title: 'Verwijderen niet mogelijk',
    message,
    confirmText: 'Ok',
    variant: 'error',
  });
}

function confirmAndDeleteGeneralLedgerGroup(
  options: DeleteGeneralLedgerGroupOptions,
): void {
  const { id, service, dialogService, destroyRef, onDeleted } = options;

  dialogService.open({
    width: '320px',
    title: 'Verwijderen?',
    message: 'Grootboekrekeningengroep verwijderen?',
    cancelText: 'Nee',
    confirmText: 'Ja',
    variant: 'error',
  });

  dialogService
    .confirmed()
    .pipe(
      take(1),
      switchMap((confirmed) => {
        if (!confirmed) {
          return EMPTY;
        }

        return service.removeGeneralLedgerGroup(id);
      }),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe({
      next: () => onDeleted(),
      error: () => undefined,
    });
}
